import * as THREE from 'three'

const _v = new THREE.Vector3()

/**
 * ClientFrames
 * ───────────–
 * Manages world-anchored UI items rendered as DOM elements overlaid on the canvas.
 * Position updates bypass React — written directly to element.style.transform each frame.
 * React only re-renders when item content changes (name, health, text, etc.).
 *
 * Scale is applied via --ui-scale CSS variable on the container element, so UI elements
 * are sized at their actual rendered pixel dimensions (no GPU upscale blur).
 *
 * Usage:
 *   const handle = client.frames.create({ type: 'unit', wx, wy, name: 'Bob', health: 80, maxHealth: 100 })
 *   handle.setPosition(wx, wy)   // cheap — just updates stored coords
 *   handle.setProps({ name: 'Alice', health: 50 })  // triggers React re-render for this item
 *   handle.destroy()
 */
export class ClientFrames {
  constructor(client) {
    this.client = client
    this.items = new Map()
    this._nextId = 0
    this._onChange = null // set by GameFrames React component
    this._scale = null
  }

  // called by GameFrames (react) to subscribe to structural changes (add/remove)
  subscribe(fn) {
    this._onChange = fn
    return () => {
      this._onChange = null
    }
  }

  create({ type, wx, wy, ...props }) {
    const id = this._nextId++
    const item = { id, type, wx, wy, props, el: null, _rerender: null }
    this.items.set(id, item)
    this._onChange?.()

    const ui = this
    return {
      setPosition(wx, wy) {
        item.wx = wx
        item.wy = wy
      },
      setProps(next) {
        Object.assign(item.props, next)
        item._rerender?.()
      },
      destroy() {
        ui.items.delete(id)
        ui._onChange?.()
      },
    }
  }

  // called every frame from Client.update()
  update(delta, time) {
    const { width, height, camera, viewSize } = this.client

    // Write scale as a CSS variable so elements are sized at actual rendered pixels —
    // keeping scale out of the CSS transform avoids GPU-layer rasterization blur.
    const scalar = 54
    const scale = height / (2 * viewSize * scalar)
    if (scale !== this._scale) {
      this._scale = scale
      document.documentElement.style.setProperty('--ui-scale', scale)
    }

    // Cull to the nearest N frames — beyond that, hide
    const MAX_FRAMES = 20
    const all = []

    for (const item of this.items.values()) {
      if (!item.el) continue
      const dx = item.wx - camera.position.x
      const dy = item.wy - camera.position.y
      item._dist2 = dx * dx + dy * dy
      all.push(item)
    }

    all.sort((a, b) => a._dist2 - b._dist2)
    for (let i = MAX_FRAMES; i < all.length; i++) {
      all[i].el.style.display = 'none'
    }

    const visible = []
    for (let i = 0; i < Math.min(all.length, MAX_FRAMES); i++) {
      const item = all[i]
      _v.set(item.wx, item.wy, 0).project(camera)
      const x = (_v.x * 0.5 + 0.5) * width
      const y = (-_v.y * 0.5 + 0.5) * height
      const isVisible = x > -300 && x < width + 300 && y > -300 && y < height + 300
      item.el.style.display = isVisible ? '' : 'none'
      if (isVisible) {
        item.el.style.transform = `translate(${x}px,${y}px)`
        item._screenY = y
        visible.push(item)
      }
    }
    // lower y on screen (higher in world) should appear on top
    visible.sort((a, b) => a._screenY - b._screenY)
    for (let i = 0; i < visible.length; i++) {
      visible[i].el.style.zIndex = i + 1
    }
  }

  destroy() {
    this.items.clear()
    this._onChange = null
  }
}
